import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import ICart from "../../../models/ICart";
import styled from "styled-components";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import IAddress from "../../../models/IAddress";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import OwButton from "../../shared/OwButton/OwButton";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import countriesWithLang from "../../../services/CountriesConstService";
import * as _ from "lodash-es";
import IUpdateCartShipToDTO from "../../../models/DTO/IUpdateCartShipToDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnUpdateCartShipToDTO from "../../../models/DTO/IOnUpdateCartShipToDTO";
import { Socket } from "socket.io-client";

type Props = {
    cart: ICart;
    socket: Socket;
    setActivityIndicator: (activityIndicator: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
};

export const EcommerceCheckoutShipTo: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const [editMode, setEditMode] = useState(false);
    const [hasShipTo, setHasShipTo] = useState(!!props.cart.Ship_To);
    const [shipTo, setShipTo] = useState(props.cart.Ship_To || ({} as IAddress));
    const [countries, setCountries] = useState<any>();

    const setupCountries = () => {
        const _countries = countriesWithLang.countries;

        const extractedCountries = _countries.map((_country) => {
            return {
                title: _country.name.common,
                value: _country.cca3,
            };
        });

        const sortedCountries = _.sortBy(extractedCountries, (country) => {
            return country.title;
        });

        const partitionedCountries = _.partition(sortedCountries, (country) => {
            // console.log(country);
            switch (country.title) {
                case "United States":
                case "Canada":
                    return true;

                default:
                    return false;
            }
        });

        const combinedPartition = partitionedCountries[0].concat(partitionedCountries[1]);

        // USA on top
        const usa = combinedPartition[1];
        const canada = combinedPartition[0];

        combinedPartition[0] = usa;
        combinedPartition[1] = canada;

        setCountries(combinedPartition);

        console.log(countries);

        const defaultValue = "USA"; // this.props.address.country
    };

    useEffect(() => {
        setupCountries();

        props.socket.on(EndpointsEnum.CartOnUpdateShipTo, onUpdateShipTo);

        return () => {
            props.socket.removeListener(EndpointsEnum.CartOnUpdateShipTo, onUpdateShipTo);
        };
    }, []);

    const changeShipTo = () => {
        setEditMode(!editMode);
    };

    const saveShipTo = () => {
        const payload: IUpdateCartShipToDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            shipTo,
        };

        props.socket.emit(EndpointsEnum.CartUpdateShipTo, payload);
    };

    const onUpdateShipTo = (data: IOnUpdateCartShipToDTO) => {
        setEditMode(false);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const existingShipTo = {
            ...shipTo,
        };

        // @ts-expect-error
        existingShipTo[name] = value;

        setShipTo(existingShipTo);
    };

    const countryOptions = [];

    return (
        <Container className={"d-flex flex-column"}>
            {editMode ? null : (
                <ShipToDisplayContainer>
                    {hasShipTo ? (
                        <div>
                            <div>{props.cart.Ship_To?.name}</div>
                            <div>{props.cart.Ship_To?.company_name}</div>
                            <div>{props.cart.Ship_To?.address_line_one}</div>
                            <div>{props.cart.Ship_To?.address_line_two}</div>
                            <div>{props.cart.Ship_To?.address_line_three}</div>
                            <div>
                                {props.cart.Ship_To?.city}, {props.cart.Ship_To?.state} {props.cart.Ship_To?.postal_code}
                            </div>
                            <div>{props.cart.Ship_To?.country}</div>
                        </div>
                    ) : (
                        <div>
                            <OwLabel title={"please_enter_a_ship_to"} />
                        </div>
                    )}
                </ShipToDisplayContainer>
            )}

            {editMode ? (
                <EditModeContainer>
                    <form autoComplete={"off"}>
                        <input type={"hidden"} autoComplete={"off"} />
                        <div>
                            <OwLabel title={"name"} className={"me-2"} />{" "}
                            <OwFnInput fn={handleChange} name={"name"} placeholder={"name"} value={shipTo.name} />
                        </div>
                        <div>
                            <OwLabel title={"company_name"} className={"me-2"} />{" "}
                            <OwFnInput fn={handleChange} placeholder={"company_name"} name={"company_name"} value={shipTo.company_name} />
                        </div>
                        <div>
                            <OwLabel title={"address"} className={"me-2"} />
                            <div>
                                <OwFnInput
                                    fn={handleChange}
                                    name={"address_line_one"}
                                    placeholder={"address_line_one"}
                                    value={shipTo.address_line_one}
                                />
                            </div>
                            <div className={"mt-2"}>
                                <OwFnInput
                                    fn={handleChange}
                                    name={"address_line_two"}
                                    placeholder={"address_line_two"}
                                    value={shipTo.address_line_two}
                                />
                            </div>
                            <div className={"mt-2"}>
                                <OwFnInput
                                    fn={handleChange}
                                    name={"address_line_three"}
                                    placeholder={"address_line_three"}
                                    value={shipTo.address_line_three}
                                />
                            </div>
                        </div>
                        <div>
                            <OwLabel title={"city"} className={"me-2"} /> {shipTo.city}
                            <OwFnInput fn={handleChange} name={"city"} placeholder={"city"} value={shipTo.city} />
                        </div>
                        <div>
                            <OwLabel title={"state"} className={"me-2"} /> {shipTo.state}
                            <OwFnInput fn={handleChange} name={"state"} placeholder={"state"} value={shipTo.state} />
                        </div>
                        <div>
                            <OwLabel title={"postal_code"} className={"me-2"} />{" "}
                            <OwFnInput fn={handleChange} name={"postal_code"} placeholder={"postal_code"} value={shipTo.postal_code} />
                        </div>
                        <div>
                            <OwLabel title={"country"} className={"me-2"} />
                            <OwSelectOption
                                name={"country"}
                                fn={handleChange}
                                options={countries}
                                selectedOption={shipTo}
                                selectedOptionProp={"country"}
                                valueProp={"value"}
                                titleProp={"title"}
                            />
                            {/*<OwFnInput fn={handleChange} name={'country'} placeholder={"country"} value={shipTo.country}/>*/}
                        </div>
                    </form>
                    <div>
                        <div>
                            <OwButton title={"save"} fn={saveShipTo} />
                        </div>
                    </div>
                </EditModeContainer>
            ) : null}

            <div className={"mt-2 mt-md-0"}>{!editMode ? <OwLabel title={"change"} fn={changeShipTo} /> : null}</div>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
`;

const ShipToDisplayContainer = styled.div`
    margin-right: 1rem;
`;

const EditModeContainer = styled.div`
    width: 20rem;
    margin-right: 1rem;

    & > div {
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;
    }
`;
